import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  effect,
  ElementRef,
  input,
  model,
  output,
  signal,
  TemplateRef,
  viewChild,
  viewChildren,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown } from '@fortawesome/pro-duotone-svg-icons/faChevronDown';
import { faInfoCircle } from '@fortawesome/pro-duotone-svg-icons/faInfoCircle';
import { TooltipDirective } from '../../ui/tooltip/tooltip.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { ContextMenu } from '../../ui/context-menu/context-menu';

export type SelectOption = {
  name: string;
  id: string;
  tag?: string;
};

@Component({
  selector: 'andor-select-field',
  standalone: true,
  imports: [TranslatePipe, FontAwesomeModule, TooltipDirective, ContextMenu, NgTemplateOutlet],
  templateUrl: './select-field.html',
  styleUrl: './select-field.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectField {
  title = input('');
  placeholder = input('');
  required = input(false);
  /** Disable for fields that require an explicit user choice, even with one option. */
  autoSelectFirst = input(true);
  options = model<SelectOption[]>([{ name: 'Default', tag: 'default', id: 'default' }]);
  disabled = model(false);
  visibleOptionsCount = input(4);
  preferredDirection = input<'top' | 'bottom' | 'auto'>('auto');
  toolTipMsg = input('');
  /**
   * Optional info badge rendered inside the field, in the slot where the
   * chevron would sit. When set, an info icon appears with this string as its
   * tooltip — useful to explain why a field is read-only/managed elsewhere.
   * Unlike `toolTipMsg` (which adds an icon next to the title), this keeps the
   * title row height unchanged so fields stay vertically aligned.
   */
  infoTooltip = input('');
  selectedOption = model<SelectOption | null>(null);
  selected = output<SelectOption>();
  forceDisabled = input(false);
  /** Optional #optionIcon template; its implicit context is the option being rendered. */
  protected readonly optionIconTemplate =
    contentChild<TemplateRef<{ $implicit: SelectOption }>>('optionIcon');

  // The dropdown lives in a body-portaled context-menu, so positioning,
  // viewport-flipping, scroll/resize tracking, outside-click and Escape are all
  // handled by `andor-context-menu`. This component only owns the trigger, the
  // option list, and two list-only concerns: capping the visible height to
  // `visibleOptionsCount` rows and the marquee ticker for overflowing names.
  private readonly menu = viewChild.required(ContextMenu);
  private readonly selectTrigger = viewChild<ElementRef<HTMLElement>>('selectTrigger');
  private readonly optionsContainer = viewChild<ElementRef<HTMLElement>>('optionsContainer');
  private readonly optionNames = viewChildren<ElementRef<HTMLElement>>('optionName');
  private readonly optionRows = viewChildren<ElementRef<HTMLElement>>('optionRow');

  readonly faChevronDown = faChevronDown;
  readonly faInfoCircle = faInfoCircle;
  readonly faCheck = faCheck;

  readonly visibleOptionsCountSafe = computed(() =>
    Math.max(1, Math.floor(this.visibleOptionsCount()))
  );
  // 'top'/'bottom' map to the context-menu's preferred side; it still flips
  // toward whichever side has room when the preferred one doesn't fit.
  readonly menuYPosition = computed<'above' | 'below'>(() =>
    this.preferredDirection() === 'top' ? 'above' : 'below'
  );

  // The trigger label is resolved from `options` by the selected id, so a
  // `selectedOption` snapshot with a stale name (e.g. the default audio device
  // after the OS default changed) can't leak into the UI. Falls back to the
  // snapshot itself when its id isn't in the list (unplugged device).
  protected readonly displayedOption = computed(() => {
    const selected = this.selectedOption();
    if (!selected) return null;
    return this.options().find((option) => option.id === selected.id) ?? selected;
  });

  readonly open = signal(false);
  readonly optionsMaxHeightPx = signal<number | null>(null);
  // Set by `onOpened`, consumed by the focus effect below. Needed because on
  // the FIRST open the projected panel content only renders after the
  // `opened` event (ContextMenu gates it behind `hasBeenOpened`), so the
  // option rows don't exist yet inside `onOpened` itself.
  #focusOnOpen = false;

  constructor() {
    effect(() => {
      if (this.forceDisabled() || this.options().length < (this.autoSelectFirst() ? 2 : 1)) {
        this.disabled.set(true);
      } else {
        this.disabled.set(false);
      }
    });
    effect(() => {
      if (this.autoSelectFirst() && !this.selectedOption() && this.options().length >= 1) {
        this.selectedOption.set(this.options()[0]);
      }
    });
    // Recompute the row-based max-height if the option list changes while the
    // dropdown is open (e.g. a device is (un)plugged mid-selection).
    effect(() => {
      this.options();
      this.visibleOptionsCountSafe();
      if (this.open()) {
        requestAnimationFrame(() => this.updateOptionsMaxHeight());
      }
    });
    // Move focus onto the active (or first) row as soon as the rows exist, so
    // arrow keys work immediately. The panel is portaled to the end of
    // <body>, so without this, keyboard users could never reach the options
    // via Tab at all.
    effect(() => {
      const rows = this.optionRows();
      if (!this.open() || !rows.length || !this.#focusOnOpen) return;
      this.#focusOnOpen = false;
      const selectedIndex = this.options().findIndex((o) => o.id === this.selectedOption()?.id);
      rows[Math.max(0, selectedIndex)]?.nativeElement.focus();
    });
  }

  protected toggleOpen() {
    if (this.disabled()) return;
    const trigger = this.selectTrigger()?.nativeElement;
    if (trigger) this.menu().toggle(trigger);
  }

  /**
   * Keyboard support on the (non-native) trigger: Enter/Space/arrows open the
   * dropdown, whose active row then takes focus (see `onOpened`). The events
   * must not bubble — surrounding surfaces treat a stray Enter as "advance"
   * (e.g. the setup wizard's Enter-to-continue) and Space would scroll.
   */
  protected onTriggerKeydown(event: KeyboardEvent) {
    if (this.disabled()) return;
    if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      this.toggleOpen();
    }
  }

  /** Roving focus inside the dropdown: arrows move, Enter/Space picks. */
  protected onOptionKeydown(event: KeyboardEvent, option: SelectOption, index: number) {
    const rows = this.optionRows();
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        event.stopPropagation();
        this.selectOption(option);
        break;
      case 'ArrowDown':
        event.preventDefault();
        rows[Math.min(index + 1, rows.length - 1)]?.nativeElement.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        rows[Math.max(index - 1, 0)]?.nativeElement.focus();
        break;
    }
  }

  protected selectOption(option: SelectOption) {
    this.selected.emit(option);
    this.selectedOption.set(option);
    this.menu().close();
    // Return focus to the trigger so keyboard flow continues from the field
    // (mirrors a native <select>; mouse users don't see a ring thanks to
    // :focus-visible).
    this.selectTrigger()?.nativeElement.focus();
  }

  protected onOpened() {
    this.open.set(true);
    this.#focusOnOpen = true;
    // The panel has just been portaled to <body> and made visible; measure on
    // the next frame so layout (incl. the trigger-matched width) has settled.
    requestAnimationFrame(() => {
      this.updateOptionsMaxHeight();
      this.checkOverflow();
    });
  }

  protected onClosed() {
    this.open.set(false);
  }

  private checkOverflow() {
    this.optionNames().forEach((ref) => {
      const el = ref.nativeElement;
      const overflow = el.scrollWidth - el.clientWidth;
      if (overflow > 0) {
        el.classList.add('is-overflowing');
        el.style.setProperty('--ticker-offset', `-${overflow}px`);
        el.style.setProperty('--ticker-duration', `${Math.max(3, overflow / 20)}s`);
      } else {
        el.classList.remove('is-overflowing');
        el.style.removeProperty('--ticker-offset');
        el.style.removeProperty('--ticker-duration');
      }
    });
  }

  private updateOptionsMaxHeight() {
    const container = this.optionsContainer()?.nativeElement;
    const rows = this.optionRows();
    const visibleCount = Math.min(this.visibleOptionsCountSafe(), rows.length);

    if (!container || !visibleCount) {
      this.optionsMaxHeightPx.set(null);
      return;
    }

    const lastVisibleRow = rows[visibleCount - 1].nativeElement;
    const containerStyles = getComputedStyle(container);
    const paddingBottom = Number.parseFloat(containerStyles.paddingBottom) || 0;
    this.optionsMaxHeightPx.set(
      lastVisibleRow.offsetTop + lastVisibleRow.offsetHeight + paddingBottom + 1
    );
  }
}
